import { useListaDeChamados } from '../../../api/queries/chamados'
import { Botao, BotaoLink } from '../../../components/Botao'
import { AvisoDeErro, Esqueleto, EstadoVazio } from '../../../components/Estados'
import { IconeBusca, IconeChamado, IconeIndicador } from '../../../components/Icones'
import { TERMOS, TEXTOS } from '../../../lib/glossario'
import { CarimboDeProcedencia } from '../CarimboDeProcedencia'
import { BotaoDeCarga } from '../importacao/BotaoDeCarga'
import { dataDeCorte, procedenciasDe } from '../procedencia'
import { ChipsDeTriagem } from './ChipsDeTriagem'
import { FiltrosDaLista } from './FiltrosDaLista'
import { ListagemDeChamados } from './ListagemDeChamados'
import { RodapeDaListagem } from './RodapeDaListagem'
import { useFiltrosNaUrl } from './filtros'
import { useLembrarBusca } from '../../../lib/ultimaBusca'

/**
 * A lista de chamados fiscalizáveis do contrato.
 *
 * O carimbo de procedência fica acima de tudo e não some nunca. É a primeira coisa da tela porque é
 * a primeira coisa que muda a leitura do resto: 64 linhas sem data de corte se leem como "o
 * contrato teve 64 chamados", e o número sai com aparência de certo. Ele é montado a partir do que
 * ESTA tela sabe — as procedências e a coleta mais recente dos chamados que ela está mostrando —,
 * porque um carimbo que afirmasse mais do que a tela mediu seria o próprio defeito que ele existe
 * para evitar. Quem quer a janela de dado de um mês inteiro tem o fechamento, e o link está ali ao
 * lado.
 *
 * @returns a listagem de chamados
 */
export function PaginaListaChamados() {
  const { filtros, alterar, limpar, temFiltro } = useFiltrosNaUrl()
  useLembrarBusca('/chamados')
  const { data: pagina, isPending, isError, error } = useListaDeChamados(filtros)
  const itens = pagina?.conteudo ?? []

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="titulo-da-pagina">{TERMOS.chamado.rotuloPlural}</h1>
          <p className="mt-1 max-w-prose text-sm text-slate-600">{TEXTOS.chamados.ajuda}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <BotaoLink to="/fiscalizacao">
            <IconeIndicador className="h-4 w-4" />
            {TERMOS.fechamentoDoMes.rotulo}
          </BotaoLink>
          <BotaoDeCarga aparencia="secundario" />
        </div>
      </header>

      <CarimboDeProcedencia
        origens={procedenciasDe(itens.map((chamado) => chamado.origem))}
        coletaMaisRecente={dataDeCorte(itens.map((chamado) => chamado.coletadoEm))}
        preferencia="lista-de-chamados"
      />

      <ChipsDeTriagem filtros={filtros} alterar={alterar} />
      <FiltrosDaLista filtros={filtros} alterar={alterar} limpar={limpar} temFiltro={temFiltro} />

      {isPending && (
        <div className="cartao px-4 py-5">
          <Esqueleto linhas={9} />
        </div>
      )}
      {isError && <AvisoDeErro erro={error} titulo="Não foi possível carregar a lista" />}

      {pagina && itens.length === 0 && (
        <EstadoVazio
          icone={temFiltro ? <IconeBusca className="h-9 w-9" /> : <IconeChamado className="h-9 w-9" />}
          titulo={temFiltro ? TEXTOS.chamados.semResultados.titulo : TEXTOS.chamados.listaVazia.titulo}
          ajuda={temFiltro ? TEXTOS.chamados.semResultados.ajuda : TEXTOS.chamados.listaVazia.ajuda}
          // Sem filtro, a lista vazia não é resultado de busca: é o sistema dizendo que ninguém
          // carregou planilha nenhuma. A saída é a carga, e ela fica aqui, no lugar em que a
          // pergunta nasce.
          acao={temFiltro ? <Botao onClick={limpar}>{TEXTOS.limparFiltros}</Botao> : <BotaoDeCarga />}
        />
      )}

      {pagina && itens.length > 0 && (
        <>
          <ListagemDeChamados itens={itens} />
          <RodapeDaListagem
            pagina={filtros.pagina}
            totalPaginas={pagina.totalPaginas}
            totalElementos={pagina.totalElementos}
            tamanho={filtros.tamanho}
            aoMudar={(numero) => alterar({ pagina: numero })}
            aoMudarTamanho={(quantidade) => alterar({ tamanho: quantidade })}
          />
        </>
      )}
    </div>
  )
}
